import { lazy, type LazyExoticComponent, type ComponentType } from 'react';
import { ROUTES } from '@shared/constants/routes';
import { Role } from '@core/auth/types/auth.types';

// Lazy load pages for code splitting
const HomePage = lazy(() =>
  import('@features/home/HomePage').then((m) => ({ default: m.HomePage }))
);

const RegisterPage = lazy(() =>
  import('@features/auth/RegisterPage').then((m) => ({
    default: m.RegisterPage,
  }))
);

const VerifyEmailPage = lazy(() =>
  import('@features/auth/VerifyEmailPage').then((m) => ({
    default: m.VerifyEmailPage,
  }))
);

const LoginPage = lazy(() =>
  import('@features/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);

const DashboardPage = lazy(() =>
  import('@features/dashboard/DashboardPage').then((m) => ({
    default: m.DashboardPage,
  }))
);

const ForgotPasswordPage = lazy(() =>
  import('@features/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);

const ResetPasswordPage = lazy(() =>
  import('@features/auth/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  }))
);

const ResendVerificationPage = lazy(() =>
  import('@features/auth/ResendVerificationPage').then((m) => ({
    default: m.ResendVerificationPage,
  }))
);

const CatalogPage = lazy(() =>
  import('@features/catalog/CatalogPage').then((m) => ({
    default: m.CatalogPage,
  }))
);

const ManualOrderPage = lazy(() =>
  import('@features/manual-order/ManualOrderPage').then((m) => ({
    default: m.ManualOrderPage,
  }))
);

const TrackOrderPage = lazy(() =>
  import('@features/order-tracking/TrackOrderPage').then((m) => ({
    default: m.TrackOrderPage,
  }))
);

const ThemesPage = lazy(() =>
  import('@features/themes/ThemesPage').then((m) => ({
    default: m.ThemesPage,
  }))
);

const RecommendationsPage = lazy(() =>
  import('@features/recommendations/RecommendationsPage').then((m) => ({
    default: m.RecommendationsPage,
  }))
);

const ProductDetailPage = lazy(() =>
  import('@features/product').then((m) => ({
    default: m.ProductDetailPage,
  }))
);

const CartPage = lazy(() =>
  import('@features/cart').then((m) => ({
    default: m.CartPage,
  }))
);

const CheckoutPage = lazy(() =>
  import('@features/checkout').then((m) => ({
    default: m.CheckoutPage,
  }))
);

const PaymentSuccessPage = lazy(() =>
  import('@features/checkout/pages/PaymentSuccessPage').then((m) => ({
    default: m.PaymentSuccessPage,
  }))
);

const PaymentFailurePage = lazy(() =>
  import('@features/checkout/pages/PaymentFailurePage').then((m) => ({
    default: m.PaymentFailurePage,
  }))
);

const PaymentPendingPage = lazy(() =>
  import('@features/checkout/pages/PaymentPendingPage').then((m) => ({
    default: m.PaymentPendingPage,
  }))
);

const OrdersPage = lazy(() =>
  import('@features/orders').then((m) => ({
    default: m.OrdersPage,
  }))
);

const OrderDetailPage = lazy(() =>
  import('@features/orders').then((m) => ({
    default: m.OrderDetailPage,
  }))
);

const ProfilePage = lazy(() =>
  import('@features/profile').then((m) => ({
    default: m.ProfilePage,
  }))
);

const ShippingInfoPage = lazy(() =>
  import('@features/info').then((m) => ({
    default: m.ShippingInfoPage,
  }))
);

const FAQPage = lazy(() =>
  import('@features/info').then((m) => ({
    default: m.FAQPage,
  }))
);

const PrivacyPolicyPage = lazy(() =>
  import('@features/info').then((m) => ({
    default: m.PrivacyPolicyPage,
  }))
);

// Vendor pages
const VendorLayout = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorLayout }))
);

const VendorDashboardPage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorDashboardPage }))
);

const VendorProductsPage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorProductsPage }))
);

const VendorProductCreatePage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorProductCreatePage }))
);

const VendorProductEditPage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorProductEditPage }))
);

const VendorOrdersPage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorOrdersPage }))
);

const VendorOrderDetailPage = lazy(() =>
  import('@features/vendor').then((m) => ({ default: m.VendorOrderDetailPage }))
);

// Admin pages
const AdminLayout = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminLayout }))
);

const AdminDashboardPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminDashboardPage }))
);

const AdminProductsPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminProductsPage }))
);

const AdminProductCreatePage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminProductCreatePage }))
);

const AdminProductEditPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminProductEditPage }))
);

const AdminOrdersPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminOrdersPage }))
);

const AdminOrderDetailPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminOrderDetailPage }))
);

const AdminAttributesPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminAttributesPage }))
);

const AdminCouponsPage = lazy(() =>
  import('@features/coupons').then((m) => ({ default: m.AdminCouponsPage }))
);

const AdminTemplatesPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminTemplatesPage }))
);

const AdminShippingPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminShippingPage }))
);

const AdminCreateOrderPage = lazy(() =>
  import('@features/admin').then((m) => ({ default: m.AdminCreateOrderPage }))
);

export interface RouteConfig {
  path: string;
  element: LazyExoticComponent<ComponentType>;
  protected?: boolean;
  guestOnly?: boolean; // Redirect authenticated users away from this route
  layout?: 'main' | 'auth' | 'none' | 'vendor' | 'admin';
  requiredRoles?: Role[];
  children?: RouteConfig[];
}

export const routes: RouteConfig[] = [
  {
    path: ROUTES.HOME,
    element: HomePage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.REGISTER,
    element: RegisterPage,
    protected: false,
    guestOnly: true,
    layout: 'main',
  },
  {
    path: ROUTES.VERIFY_EMAIL,
    element: VerifyEmailPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.LOGIN,
    element: LoginPage,
    protected: false,
    guestOnly: true,
    layout: 'main',
  },
  {
    path: ROUTES.DASHBOARD,
    element: DashboardPage,
    protected: true,
    layout: 'main',
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: ForgotPasswordPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: ResetPasswordPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.RESEND_VERIFICATION,
    element: ResendVerificationPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.CATALOG,
    element: CatalogPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.MANUAL_ORDER,
    element: ManualOrderPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.TRACK_ORDER,
    element: TrackOrderPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.THEMES,
    element: ThemesPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.RECOMMENDATIONS,
    element: RecommendationsPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.PRODUCT_DETAIL,
    element: ProductDetailPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.CART,
    element: CartPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.CHECKOUT,
    element: CheckoutPage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.CHECKOUT_SUCCESS,
    element: PaymentSuccessPage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.CHECKOUT_FAILURE,
    element: PaymentFailurePage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.CHECKOUT_PENDING,
    element: PaymentPendingPage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.ORDERS,
    element: OrdersPage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.ORDER_DETAIL,
    element: OrderDetailPage,
    protected: true,
    layout: 'main',
    requiredRoles: [Role.CUSTOMER, Role.VENDOR, Role.ADMIN],
  },
  {
    path: ROUTES.PROFILE,
    element: ProfilePage,
    protected: true,
    layout: 'main',
  },
  {
    path: ROUTES.SHIPPING,
    element: ShippingInfoPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.FAQ,
    element: FAQPage,
    protected: false,
    layout: 'main',
  },
  {
    path: ROUTES.PRIVACY,
    element: PrivacyPolicyPage,
    protected: false,
    layout: 'main',
  },
];

// Vendor routes (nested under VendorLayout)
export const vendorRoutes: RouteConfig[] = [
  {
    path: ROUTES.VENDOR.DASHBOARD,
    element: VendorDashboardPage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.VENDOR.PRODUCTS,
    element: VendorProductsPage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.VENDOR.PRODUCT_CREATE,
    element: VendorProductCreatePage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.VENDOR.PRODUCT_EDIT,
    element: VendorProductEditPage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.VENDOR.ORDERS,
    element: VendorOrdersPage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.VENDOR.ORDER_DETAIL,
    element: VendorOrderDetailPage,
    protected: true,
    layout: 'vendor',
    requiredRoles: [Role.ADMIN],
  },
];

// Admin routes (nested under AdminLayout)
export const adminRoutes: RouteConfig[] = [
  {
    path: ROUTES.ADMIN.DASHBOARD,
    element: AdminDashboardPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.PRODUCTS,
    element: AdminProductsPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.PRODUCT_CREATE,
    element: AdminProductCreatePage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.PRODUCT_EDIT,
    element: AdminProductEditPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.ORDERS,
    element: AdminOrdersPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    // Must precede ORDER_DETAIL so "/admin/orders/new" doesn't match :id
    path: ROUTES.ADMIN.ORDER_CREATE,
    element: AdminCreateOrderPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.ORDER_DETAIL,
    element: AdminOrderDetailPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.ATTRIBUTES,
    element: AdminAttributesPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.COUPONS,
    element: AdminCouponsPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.SHIPPING,
    element: AdminShippingPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
  {
    path: ROUTES.ADMIN.TEMPLATES,
    element: AdminTemplatesPage,
    protected: true,
    layout: 'admin',
    requiredRoles: [Role.ADMIN],
  },
];

export { ROUTES, VendorLayout, AdminLayout, Role };
